import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArbolPendientes, fmtMonto } from '../agrupadores/ArbolPendientes';
import type { EgresoPendiente, Moneda, NodoEgresos } from '../../types';

interface Props {
  nodos: NodoEgresos[];
  moneda: Moneda | 'todas';
  /** Con búsqueda activa conviene ver todo abierto. */
  expandirTodo?: boolean;
  canPagar: boolean;
  disabled?: boolean;
  onPagarNodo: (nodo: NodoEgresos, moneda: Moneda) => void;
  onPagarItem: (item: EgresoPendiente) => void;
}

export function ArbolEgresosPendientes({ nodos, moneda, expandirTodo = false, canPagar, disabled = false, onPagarNodo, onPagarItem }: Props) {
  return (
    <ArbolPendientes
      nodos={nodos}
      moneda={moneda}
      expandirTodo={expandirTodo}
      verbo="Pagar"
      vacio="Sin egresos para los filtros seleccionados."
      canAccionar={canPagar}
      disabled={disabled}
      onAccionNodo={onPagarNodo}
      renderItem={(item, profundidad) => (
        <ItemRow
          item={item}
          profundidad={profundidad}
          canPagar={canPagar}
          disabled={disabled}
          onPagar={() => onPagarItem(item)}
        />
      )}
    />
  );
}

function ItemRow({ item, profundidad, canPagar, disabled, onPagar }: {
  item: EgresoPendiente;
  profundidad: number;
  canPagar: boolean;
  disabled: boolean;
  onPagar: () => void;
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-dashed border-gray-100 bg-slate-50/50 py-2 pr-4"
      style={{ paddingLeft: `${40 + profundidad * 20}px` }}
    >
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm text-gray-900">
          {item.tramiteId ? (
            <Link
              to="/dashboard"
              state={{ tramite: { id: item.tramiteId } }}
              className="mr-1.5 font-medium text-primary hover:underline"
            >
              #{item.tramiteId}
            </Link>
          ) : null}
          {item.tramitePropiedad ? `${item.tramitePropiedad} - ${item.itemNombre}` : item.itemNombre}
        </p>
        <p className="text-xs text-muted-foreground">
          {item.clienteNombre ? `${item.clienteNombre} · ` : ''}{item.fecha}
        </p>
      </div>
      <div className="text-right leading-tight">
        <p className="text-sm font-semibold tabular-nums text-slate-600">{fmtMonto(item.saldoRestante, item.moneda)}</p>
        {item.totalPagado > 0 && (
          <p className="text-[11px] tabular-nums text-blue-600">pagado {fmtMonto(item.totalPagado, item.moneda)}</p>
        )}
      </div>
      {canPagar && (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          className="h-7 rounded-lg px-2.5 text-xs"
          onClick={onPagar}
          aria-label={`Pagar ${item.itemNombre}`}
        >
          Pagar
        </Button>
      )}
    </div>
  );
}
